import React, { useState, useCallback } from 'react';

import { Accordion, AccordionContent, AccordionTitle, Ratings } from '@hpstellar/core';
import VisIDAccordion, {
    AccordionBody as VisIDAccordionBody,
    AccordionHeader as VisIDAccordionHeader,
} from '@hpstellar/core/visId/Accordion';

import Facet, { VisIDFacet } from './vwa-facet';
import { clickOnEnter } from '../../ui/util';
import { withError } from '../../shared/components/error-boundary';
import { Helpers } from '../../core/src/helpers';

import './vwa-facet-accordion.less';

export default withError(props => {
    const {
        facet,
        idx,
        expanded,
        tooltipPosition,
        onSelectFacet,
        aligned,
        truncationSettings,
        showZeroCount = true,
        visId,
        hideEmpty = false,
    } = props;
    const AccordionComponent = visId ? VisIDAccordion : Accordion;
    const AccordionTitleComponent = visId ? VisIDAccordionHeader : AccordionTitle;
    const AccordionContentComponent = visId ? VisIDAccordionBody : AccordionContent;

    const { truncationExpandText = 'Show more options', truncationCollapseText = 'Show less options' } =
        truncationSettings || {};
    const {
        tooltip,
        displayName,
        facetValue,
        key: facetKey,
        className,
        FilterComponent,
        FilterSetComponent,
        truncateThreshold,
        facetProps,
        alias,
        isCollapsible,
        accordionBodyClassName,
        valMap,
    } = facet || {};
    const isRating = facetKey === 'rating';

    const numFacetValues = facetValue && facetValue.length;

    const validFacetValues = Array.isArray(facetValue);
    const isTruncated = truncateThreshold && Number(truncateThreshold) > 0 && numFacetValues > truncateThreshold;
    const numHidableFacets = numFacetValues - truncateThreshold;

    const toggleTruncatedFacets = useCallback(() => {
        setTruncateExpanded(!truncateExpanded);
    });

    let hasSelected;
    const truncatedFacetValues = [];
    const FacetComponent = visId ? VisIDFacet : Facet;

    const facetValues =
        !FilterSetComponent &&
        validFacetValues &&
        facetValue.reduce(
            (
                allFacetValues,
                { displayName: label, checked, count, urlValue, gtmActions, facetKeyOverride, className, canonical },
                idx,
            ) => {
                const overrides = valMap?.[label];
                if (overrides) {
                    if (overrides.hide) return allFacetValues;
                    if (overrides.labelOverride) label = overrides.labelOverride;
                }

                let zeroCount = count + '' === '0';
                if (zeroCount && !showZeroCount) return allFacetValues;

                let filterProps = {
                    key: idx,
                    label: isRating ? undefined : label,
                    checked: checked === 'true',
                    canonical,
                    counter: count,
                    onChange: onSelectFacet(facetKeyOverride || facetKey, urlValue),
                    readOnly: zeroCount,
                    aligned,
                    className: `${isRating ? 'ratingCheckbox' : ''}${className ? ' ' + className : ''}`,
                    gtmActions,
                    ...(facetProps || {}),
                };

                hasSelected = hasSelected || filterProps.checked;

                const component = FilterComponent ? (
                    <FilterComponent {...filterProps} />
                ) : (
                    <FacetComponent {...filterProps}>
                        {isRating && (
                            <Ratings
                                className="filterRating"
                                score={label === 'None' ? 0 : label.substring(0, 1)}
                                showScore={false}
                            />
                        )}
                    </FacetComponent>
                );

                if (isTruncated && idx < truncateThreshold) {
                    truncatedFacetValues.push(component);
                }
                allFacetValues.push(component);

                return allFacetValues;
            },
            [],
        );

    const [truncateExpanded, setTruncateExpanded] = useState(hasSelected);

    const accordionContent = (
        <AccordionContentComponent
            className={`accordionContent`}
            {...(accordionBodyClassName ? { activeClassName: accordionBodyClassName } : {})}
        >
            {FilterSetComponent ? (
                <FilterSetComponent {...props} {...(facetProps || {})} />
            ) : (
                <>
                    {isTruncated && !truncateExpanded ? truncatedFacetValues : facetValues}
                    {validFacetValues && isTruncated && (
                        <div>
                            <a onClick={toggleTruncatedFacets} className="vwaTruncateButton">
                                {truncateExpanded ? truncationCollapseText : truncationExpandText}
                            </a>
                        </div>
                    )}
                </>
            )}
        </AccordionContentComponent>
    );

    const accordionComponentProps = visId
        ? {
              borderPosition: 'bottom',
          }
        : { size: 'small' };

    const tooltipDscription = <span dangerouslySetInnerHTML={Helpers.createMarkup(tooltip)} />;
    const accordionTitleProps = visId
        ? {
              className: 'visIdAccordionTitle',
              ...(tooltip
                  ? {
                        TooltipProps: {
                            position: 'left',
                            content: tooltipDscription,
                            title: displayName,
                        },
                    }
                  : {}),
          }
        : {
              tooltipPosition,
              ...(tooltip
                  ? {
                        tooltip: {
                            description: tooltipDscription,
                            title: Helpers.stringContainsHTML(tooltip) ? ' ' : undefined,
                        },
                    }
                  : {}),
          };

    if (facetValues.length < 1 && hideEmpty) {
        return null;
    }

    return (
        <AccordionComponent
            id={`facet_${idx}`}
            className={`accordion${className ? ' ' + className : ''}${visId ? ' visIdAccordion' : ''}`}
            defaultExpanded={(expanded && expanded[(alias || displayName).toLowerCase()]) || facet.expanded}
            {...(isCollapsible === false ? { expanded: true } : {})}
            {...accordionComponentProps}
        >
            <AccordionTitleComponent
                {...accordionTitleProps}
                onKeyUp={clickOnEnter}
                tag="h4"
                title={displayName}
            ></AccordionTitleComponent>
            {accordionContent}
        </AccordionComponent>
    );
});
